import { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Page,
  Text,
  TextField,
} from '@shopify/polaris';

import { api } from '@/api/client';

interface TestStore {
  domain: string;
  note: string | null;
  created_at: number;
}

/**
 * The owner's own stores, kept out of the numbers.
 *
 * ⚠ Early on these ARE most of the installs, so a dashboard that counts them is
 * a dashboard that lies exactly when the numbers matter most — at the start,
 * when there is nothing else to go on.
 */
export function TestStoresPage() {
  const [stores, setStores] = useState<TestStore[]>([]);
  const [domain, setDomain] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    void api<{ stores: TestStore[] }>('/ops/api/test-stores')
      .then((r) => setStores(r.stores ?? []))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const add = useCallback(async () => {
    setError(null);
    try {
      await api('/ops/api/test-stores', {
        method: 'POST',
        body: JSON.stringify({ domain: domain.trim(), note: note.trim() }),
      });
      setDomain('');
      setNote('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [domain, note, load]);

  const remove = useCallback(
    async (d: string) => {
      try {
        await api(`/ops/api/test-stores/${encodeURIComponent(d)}`, { method: 'DELETE' });
        load();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [load]
  );

  return (
    <Page title="Test stores" subtitle="Excluded from every number on the dashboard.">
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            <p>{error}</p>
          </Banner>
        )}
        <Card>
          <BlockStack gap="300">
            <TextField
              label="Domain"
              value={domain}
              onChange={setDomain}
              autoComplete="off"
              placeholder="confetto-dev-01.myshopify.com"
            />
            <TextField label="Note" value={note} onChange={setNote} autoComplete="off" />
            <InlineStack>
              <Button variant="primary" disabled={!domain.trim()} onClick={() => void add()}>
                Add
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="200">
            {stores.length === 0 && (
              <Text as="p" tone="subdued">
                None yet.
              </Text>
            )}
            {stores.map((s) => (
              <InlineStack key={s.domain} align="space-between" blockAlign="center">
                <BlockStack gap="050">
                  <Text as="span" fontWeight="semibold">
                    {s.domain}
                  </Text>
                  {s.note && (
                    <Text as="span" tone="subdued" variant="bodySm">
                      {s.note}
                    </Text>
                  )}
                </BlockStack>
                <Button variant="plain" tone="critical" onClick={() => void remove(s.domain)}>
                  Remove
                </Button>
              </InlineStack>
            ))}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
